import { Button, type SelectOption } from '@ph-react-ui/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetSchoolYearBaseSettings } from '../../../../hooks/settings/useGetSchoolYearBaseSettings';
import { useGetInstallmentsByStudent } from '../../../../hooks/taxes/school-taxes/parent/history/useGetInstallmentsByStudent';
import { useModal } from '../../../../hooks/useModal';
import { Select } from '../../../../utils/components/hoc-components';
import { PARTIAL_PAYMENT } from '../../../../utils/constants/taxes/modals';
import { displayPrice } from '../../../../utils/displayPrice';
import { Dialog } from '../../../shared/components/Dialog';
import { isPenaltyApplied } from './utils/schoolTaxesUtils';

export const PartialPaymentModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const name = searchParams.get('name');
  const yearParam = searchParams.get('year');

  const { close: closeModal } = useModal(PARTIAL_PAYMENT);
  const { data: schoolYearSettings } = useGetSchoolYearBaseSettings();
  const currentSchoolYear = schoolYearSettings?.startOfSchoolYear?.year;

  const parsedYear = yearParam ? Number(yearParam) : undefined;
  const startYearForFetch =
    Number.isFinite(parsedYear ?? NaN) && parsedYear !== undefined
      ? parsedYear
      : currentSchoolYear ?? undefined;

  const { data: installments } = useGetInstallmentsByStudent(
    id ?? '',
    startYearForFetch
  );
  const [options, setOptions] = useState<SelectOption<string>[]>([]);

  const methods = useForm({
    defaultValues: {
      selectedInstallments: [],
    },
  });

  const unpaidInstallments = useMemo(() => {
    if (!installments) {
      return [];
    }
    return installments.result
      .filter((item) => !item.isPaid)
      .sort((a, b) => a.index - b.index);
  }, [installments]);

  useEffect(() => {
    if (unpaidInstallments.length === 0) {
      setOptions([]);
      return;
    }

    const firstUnpaidIndexValue = Math.min(
      ...unpaidInstallments.map((inst) => inst.index)
    );

    const options = unpaidInstallments.map((item) => {
      const amount = displayPrice(
        item.amount,
        t('TAXES.LABELS.BGN'),
        t('TAXES.LABELS.EUR'),
        item.currency
      );

      const penalty = isPenaltyApplied(item)
        ? ` - ${t('TAXES.LABELS.PENALTY')} ${displayPrice(
            item.penalty,
            t('TAXES.LABELS.BGN'),
            t('TAXES.LABELS.EUR'),
            item.currency
          )}`
        : '';

      return {
        label: `${t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENT')} ${
          item.index + 1
        } - (${amount}${penalty} - ${item.percentage}%)`,
        value: item.id,
        disabled: item.index !== firstUnpaidIndexValue,
      };
    });
    setOptions(options);
  }, [unpaidInstallments, t]);

  const selectedInstallments = methods.watch('selectedInstallments');

  const onContinuePayment = useCallback(() => {
    const params = new URLSearchParams();
    params.set('studentId', id ?? '');
    if (yearParam) {
      params.set('year', yearParam);
    }

    navigate(
      `/dashboard/payments/school-taxes/pre-payment/${selectedInstallments.join(
        '&'
      )}/${id}?${params.toString()}`
    );
  }, [navigate, selectedInstallments, id, yearParam]);

  const onSubmit = useCallback(() => {
    onContinuePayment();
  }, [onContinuePayment]);

  useEffect(() => {
    setOptions((options) =>
      options.map((item, index) => {
        return {
          ...item,
          disabled: !(
            selectedInstallments.length === index ||
            selectedInstallments.length === index + 1
          ),
        };
      })
    );
  }, [selectedInstallments.length]);

  return (
    <Dialog
      className="dialog__school-taxes__partial-payment"
      onClose={() => closeModal()}
      dialogHeader={t('PAYMENTS.LABELS.SELECT_PERIOD') ?? ''}
      actionButton={
        <Button
          disabled={!selectedInstallments || !installments?.result.length}
          type="submit"
          form="payment-modal-form"
        >
          {t('ACTIONS.CONTINUE')}
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form id="payment-modal-form" onSubmit={methods.handleSubmit(onSubmit)}>
          <h3 className="mb-3">
            {t('PAYMENTS.DIALOGS.LABELS.GOING_TO_PAY')}
            <span className="dialog__confirm__content__bold">{name}</span>
          </h3>
          {unpaidInstallments.length > 0 && (
            <h3 className="mb-8">
              {t('TAXES.TABLE.HEADERS.SCHOOL_YEAR')}:{' '}
              <span className="dialog__confirm__content__bold">
                {unpaidInstallments[0].startYear} -{' '}
                {unpaidInstallments[0].endYear}
              </span>
            </h3>
          )}
          <h3 className="mb-6">
            {t('PAYMENTS.DIALOGS.LABELS.CHOOSE_INSTALLMENTS')}
          </h3>
          <Select
            placeholder={t('PAYMENTS.DIALOGS.LABELS.SELECT_INSTALLMENTS') ?? ''}
            multi={true}
            options={options}
            name="selectedInstallments"
          />
        </form>
      </FormProvider>
    </Dialog>
  );
};
